<script setup lang="ts">
/**
 * HelpView — the user guide (INC-190, ADR-101; figures INC-198; docs/archive/features/INC-198_user-guide.md).
 * Text in i18n `guide.*`; `[[key]]` tokens render the app's current label in <strong>.
 */
import { onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { FEEDBACK_EMAIL } from '@/data/app'
import { packRegistry } from '@/content-packs/registry'
import LinkedText from '@/components/ui/LinkedText.vue'
import shots from '@/assets/imagery/guide/guide-shots.json'

// The guide's scene captures, keyed by their Vite glob path (theme and derivative width in the name).
const images = import.meta.glob('../assets/imagery/guide/*.webp', {
  eager: true,
  import: 'default',
}) as Record<string, string>
const THEMES = ['light', 'dark'] as const

const { t, tm, rt } = useI18n()
const route = useRoute()

const params = { feedback: FEEDBACK_EMAIL }

/** One figure as authored: the scene whose capture it shows, its alt text and its caption. */
interface GuideFigureSource {
  scene: string
  alt: string
  caption: string
}

/** One figure ready to render: the alt (tokens resolved) and its two theme derivatives. */
interface GuideFigure {
  alt: string
  caption: string
  images: {
    theme: string
    src: string
    srcset: string
    sizes: string
    maxWidth: string
    width: number
    height: number
  }[]
}

/** One section as authored in the message catalog (leaves are compiled messages). */
interface GuideSectionSource {
  id: string
  group: string
  title: string
  paragraphs?: string[]
  steps?: string[]
  notes?: string[]
  list?: string[]
  figure?: GuideFigureSource
  link?: string
}

/** One section with every leaf resolved to a plain string. */
interface GuideSection {
  id: string
  group: string
  title: string
  paragraphs: string[]
  steps: string[]
  notes: string[]
  list: string[]
  figure: GuideFigure | null
  link: string | null
}

/** One figure's derivative URLs and box: the capture is `W × H` pixels, shown at `c = W / 2` css
 *  px (never upscaled), `-<c>w` and `-<W>w` in the srcset. The column is `max-w-2xl` minus `px-6`. */
function buildFigure(scene: string, alt: string, caption: string): GuideFigure {
  const { width: W, height: H } = shots[scene as keyof typeof shots]
  const c = W / 2
  const url = (theme: string, w: number) =>
    images[`../assets/imagery/guide/guide-${scene}-${theme}-${w}w.webp`] ?? ''
  return {
    alt,
    caption,
    images: THEMES.map((theme) => ({
      theme,
      src: url(theme, W),
      srcset: `${url(theme, c)} ${c}w, ${url(theme, W)} ${W}w`,
      sizes: `(min-width: 672px) ${Math.min(624, c)}px, min(${c}px, calc(100vw - 48px))`,
      maxWidth: `${c}px`,
      width: W,
      height: H,
    })),
  }
}

const raw = tm('guide.sections') as unknown as GuideSectionSource[] | undefined
const sections: GuideSection[] = (raw ?? []).map((s) => ({
  id: rt(s.id),
  group: rt(s.group),
  title: rt(s.title),
  paragraphs: (s.paragraphs ?? []).map((m) => rt(m, params)),
  steps: (s.steps ?? []).map((m) => rt(m, params)),
  notes: (s.notes ?? []).map((m) => rt(m, params)),
  list: (s.list ?? []).map((m) => rt(m, params)),
  figure: s.figure
    ? buildFigure(rt(s.figure.scene), rt(s.figure.alt, params), rt(s.figure.caption, params))
    : null,
  link: s.link ? rt(s.link) : null,
}))

const GROUPS = ['start', 'gm', 'player', 'all'] as const
const groups = GROUPS.map((key) => ({ key, sections: sections.filter((s) => s.group === key) }))

// `[[key]]` inside a paragraph/step/note marks an app label to render in <strong>.
const TOKEN = /\[\[([\w.]+)\]\]/
function parts(text: string): { text: string; label: boolean }[] {
  return text
    .split(TOKEN)
    .map((part, i) =>
      i % 2 === 1 ? { text: t(part), label: true } : { text: part, label: false },
    )
    .filter((p) => p.label || p.text !== '')
}

// A figure's alt is plain text: every `[[key]]` becomes the app's label, with no markup.
const altText = (text: string) => text.replace(/\[\[([\w.]+)\]\]/g, (_, key: string) => t(key))

// The credits section lists every game in this build that carries a licence notice (ADR-109).
const credits = [...packRegistry.values()].flatMap((e) =>
  e.notice ? [{ name: e.name, notice: e.notice }] : [],
)

// INC-231: the games section lists every game in this build: its label, its one-line description,
// and a License link to the credits below when it has a notice. A restricted game is not public, and
// this page is prerendered, so it is left out (ADR-099).
const games = [...packRegistry.values()].filter((e) => !e.restricted).map((e) => ({
  name: e.name,
  blurb: e.blurb,
  notice: Boolean(e.notice),
}))

const LINKS: Record<string, string> = {
  feedback: `mailto:${FEEDBACK_EMAIL}`,
  privacy: '/privacy',
}

// No router scrollBehavior: an in-app link carrying a section hash lands here and scrolls it in,
// after the webfonts settle (they reflow the page above the section on a fresh load).
onMounted(async () => {
  const id = (route?.hash ?? '').slice(1)
  if (!id) return
  await document.fonts?.ready
  document.getElementById(id)?.scrollIntoView()
})
</script>

<template>
  <article class="mx-auto w-full max-w-2xl px-6 py-12">
    <h1 class="text-title font-bold text-fg-primary">{{ t('guide.title') }}</h1>
    <p class="mt-3 max-w-prose text-fg-secondary">{{ t('guide.intro') }}</p>

    <nav
      aria-labelledby="guide-contents"
      class="mt-8 scroll-mt-20 rounded-xl border border-border bg-surface p-5"
    >
      <h2 id="guide-contents" class="font-bold text-fg-primary">{{ t('guide.contents') }}</h2>
      <div class="mt-4 sm:columns-2 sm:gap-8">
        <div v-for="g in groups" :key="g.key" class="mb-4 break-inside-avoid last:mb-0">
          <p class="text-small font-bold text-fg-secondary">{{ t(`guide.groups.${g.key}`) }}</p>
          <!-- py-2.5: 44 px touch targets on phones (PRODUCT), tighter from sm. -->
          <ul class="mt-1">
            <li v-for="s in g.sections" :key="s.id">
              <a :href="`#${s.id}`" class="block py-2.5 text-accent-text underline sm:py-1">{{
                s.title
              }}</a>
            </li>
          </ul>
        </div>
      </div>
    </nav>

    <section v-for="g in groups" :key="g.key" :aria-labelledby="`group-${g.key}`" class="mt-12">
      <h2
        :id="`group-${g.key}`"
        class="border-b border-border pb-2 text-heading font-bold text-fg-primary"
      >
        {{ t(`guide.groups.${g.key}`) }}
      </h2>
      <div v-for="s in g.sections" :id="s.id" :key="s.id" class="mt-8 scroll-mt-20">
        <h3 class="font-bold text-fg-primary">{{ s.title }}</h3>
        <p
          v-for="(para, i) in s.paragraphs"
          :key="`p${i}`"
          class="mt-3 max-w-prose break-words text-fg-primary"
        >
          <template v-for="(part, k) in parts(para)" :key="k">
            <strong v-if="part.label" class="font-bold">{{ part.text }}</strong>
            <template v-else>{{ part.text }}</template>
          </template>
        </p>
        <ol
          v-if="s.steps.length"
          class="mt-3 max-w-prose list-decimal space-y-2 pl-6 text-fg-primary"
        >
          <li v-for="(step, i) in s.steps" :key="`s${i}`" class="break-words">
            <template v-for="(part, k) in parts(step)" :key="k">
              <strong v-if="part.label" class="font-bold">{{ part.text }}</strong>
              <template v-else>{{ part.text }}</template>
            </template>
          </li>
        </ol>
        <p
          v-for="(note, i) in s.notes"
          :key="`n${i}`"
          class="mt-3 max-w-prose break-words text-fg-primary"
        >
          <template v-for="(part, k) in parts(note)" :key="k">
            <strong v-if="part.label" class="font-bold">{{ part.text }}</strong>
            <template v-else>{{ part.text }}</template>
          </template>
        </p>
        <ul
          v-if="s.list.length"
          class="mt-3 max-w-prose list-disc space-y-2 pl-6 text-fg-primary"
        >
          <li v-for="(item, i) in s.list" :key="`l${i}`" class="break-words">
            <template v-for="(part, k) in parts(item)" :key="k">
              <strong v-if="part.label" class="font-bold">{{ part.text }}</strong>
              <template v-else>{{ part.text }}</template>
            </template>
          </li>
        </ul>
        <dl v-if="s.id === 'games'" class="mt-3 max-w-prose space-y-3">
          <div v-for="g in games" :key="g.name">
            <dt class="font-bold text-fg-primary">{{ g.name }}</dt>
            <dd class="mt-0.5 break-words text-fg-secondary">
              {{ g.blurb }}
              <a v-if="g.notice" href="#credits" class="text-accent-text underline">{{
                t('packPicker.license')
              }}</a>
            </dd>
          </div>
        </dl>
        <!-- ADR-109: each game's licence notice, verbatim from the registry, its URLs as links. -->
        <dl v-if="s.id === 'credits'" class="mt-3 max-w-prose space-y-3">
          <div v-for="c in credits" :key="c.name">
            <dt class="font-bold text-fg-primary">{{ c.name }}</dt>
            <dd class="mt-1 break-words text-fg-primary"><LinkedText :text="c.notice" /></dd>
          </div>
        </dl>
        <figure v-if="s.figure" class="mt-4">
          <img
            v-for="img in s.figure.images"
            :key="img.theme"
            class="block h-auto w-full rounded-lg border border-border"
            :class="`shot--${img.theme}`"
            :style="{ maxWidth: img.maxWidth }"
            :src="img.src"
            :srcset="img.srcset"
            :sizes="img.sizes"
            :width="img.width"
            :height="img.height"
            loading="lazy"
            decoding="async"
            :alt="altText(s.figure.alt)"
          />
          <figcaption class="mt-2 max-w-prose text-small text-fg-secondary">
            <template v-for="(part, k) in parts(s.figure.caption)" :key="k">
              <strong v-if="part.label" class="font-bold">{{ part.text }}</strong>
              <template v-else>{{ part.text }}</template>
            </template>
          </figcaption>
        </figure>
        <p v-if="s.link" class="mt-3">
          <RouterLink
            v-if="s.link === 'privacy'"
            to="/privacy"
            class="text-accent-text underline"
            >{{ t(`guide.links.${s.link}`) }}</RouterLink
          >
          <a v-else :href="LINKS[s.link]" class="text-accent-text underline">{{
            t(`guide.links.${s.link}`)
          }}</a>
        </p>
      </div>
      <!-- One way back per group (PRODUCT: a long page on a phone); the nav's `scroll-mt-20` keeps
           the heading clear of the bar. py-2.5: a 44 px touch target on phones. -->
      <p class="mt-8">
        <a
          href="#guide-contents"
          class="inline-block py-2.5 text-small text-accent-text underline sm:py-1"
          >{{ t('guide.backToContents') }}</a
        >
      </p>
    </section>

    <RouterLink
      to="/"
      class="mt-10 inline-block text-sm text-fg-secondary underline hover:text-accent-text"
    >
      {{ t('waitlistAction.home') }}
    </RouterLink>
  </article>
</template>
